import './Ecosystem.css';
import { colors, fonts } from '../theme';

const originators = [
  'Factories — production, quality, compliance',
  'Suppliers & subcontractors',
  'Third-party labs & certifiers',
  'Machines, sensors & test equipment',
  'Enterprise systems — MES, ERP, QMS',
];

const consumers = [
  'OEMs & buyers — production capability',
  'Auditors — evidence-ready packages',
  'Regulators & authorities — integration with Digital Product Passports',
  'Lenders & insurers — verified operational proof',
  'Marketplaces & procurement platforms',
];

const innerSteps = [
  { label: 'AI drafts · expert approves', title: 'Digital Specification Studio', sub: 'Extraction, formalization, expert ratification' },
  { label: 'Deterministic · reproducible', title: 'Forensic Engine', sub: 'Evidence processing, conformance, proof + explainability' },
];

const chipStyle = {
  background: colors.bgAlt,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  padding: '12px 14px',
  fontSize: 13.5,
  color: colors.slate,
};

const Technology = () => (
  <section id="technology" style={{ padding: 'clamp(56px, 8vw, 96px) clamp(20px, 5vw, 48px)', background: colors.bgAlt }}>
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ maxWidth: 640, marginBottom: 48 }}>
        <p style={{ color: colors.teal, fontSize: 12.5, fontFamily: fonts.mono, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 16 }}>Platform</p>
      </div>

      <div style={{ borderLeft: `3px solid ${colors.teal}`, padding: '4px 24px', marginBottom: 24, fontSize: 15.5, color: colors.slateDark, lineHeight: 1.7, maxWidth: 760 }}>
        We combine neuro-symbolic AI and material knowledge graphs to translate human-readable requirements into machine-verifiable logic. Every piece of evidence is cryptographically signed, making it tamper-evident and independently verifiable. The forensic engine itself reasons in three tiers — atomic checks against a single requirement, cross-correlation across independent evidence sources, and longitudinal analysis against historical pattern — with an explainable verdict at every step.
      </div>

      <p style={{ color: colors.slateDark, fontSize: 16.5, lineHeight: 1.65, maxWidth: 760, marginBottom: 48 }}>
        The execution layer isn't built for one buyer relationship — the proof it produces is built to be read by anyone downstream who currently has to take a factory's word for it.
      </p>

      <div className="eco-flow">
        <div className="eco-col">
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.teal, fontWeight: 700, marginBottom: 12 }}>Evidence sources — where it comes from</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {originators.map(o => <div key={o} style={chipStyle}>{o}</div>)}
            </div>
          </div>
        </div>

        <div className="eco-arrow" style={{ color: colors.slate }}>→</div>

        <div className="eco-center" style={{ background: colors.bg, border: `2px solid ${colors.teal}` }}>
          <div style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 19, color: colors.navy, marginBottom: 6 }}>The Execution Layer</div>
          <div style={{ fontFamily: fonts.mono, fontSize: 11.5, color: colors.slate, marginBottom: 20 }}>where conformance is computed</div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginBottom: 16 }}>
            <div style={{ background: colors.navy, borderRadius: 8, padding: '14px 16px', textAlign: 'left' }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.teal, marginBottom: 6 }}>{innerSteps[0].label}</div>
              <div style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 14, color: '#fff', marginBottom: 4 }}>{innerSteps[0].title}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>{innerSteps[0].sub}</div>
            </div>

            <div style={{ textAlign: 'center', color: colors.slate, fontSize: 14 }}>↓</div>

            <div style={{ background: colors.navy, borderRadius: 8, padding: '14px 16px', textAlign: 'left' }}>
              <div style={{ fontFamily: fonts.mono, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.teal, marginBottom: 6 }}>{innerSteps[1].label}</div>
              <div style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 14, color: '#fff', marginBottom: 4 }}>{innerSteps[1].title}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>{innerSteps[1].sub}</div>
            </div>
          </div>

          <div style={{ background: colors.navy, borderRadius: 8, padding: '14px 16px', textAlign: 'left', width: '100%' }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.teal, marginBottom: 6 }}>Factory-defined · governed</div>
            <div style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 14, color: '#fff', marginBottom: 4 }}>Operation Layer</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>Factory-defined workflows, digital gates, permissioned ledger</div>
          </div>
        </div>

        <div className="eco-arrow" style={{ color: colors.slate }}>→</div>

        <div className="eco-col">
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 14, padding: 20 }}>
            <div style={{ fontFamily: fonts.mono, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.teal, fontWeight: 700, marginBottom: 12 }}>Where the proof is read</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {consumers.map(c => <div key={c} style={chipStyle}>{c}</div>)}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 36, background: colors.navy, color: '#fff', borderRadius: 14, padding: '22px 28px', fontSize: 14.5, lineHeight: 1.6 }}>
        As verified evidence compounds across shipments and buyers, it becomes <b style={{ color: colors.teal }}>Factory Passport</b> — a capability and conformance credential for the whole factory, not just a single material or shipment.
      </div>
    </div>
  </section>
);

export default Technology;
